/**
 * Token Preservation Utility for Customer App
 * 
 * CRITICAL: This utility ensures that authentication tokens are NEVER cleared
 * except when explicitly requested by the user (logout) or when the backend
 * confirms the token is truly invalid.
 * 
 * DO NOT clear tokens on:
 * - Network errors
 * - Server errors (500, 503, etc.)
 * - Temporary authentication issues
 * - Any operation errors (bookings, cart, orders, etc.)
 * - API call failures
 */

const TOKEN_CLEARING_RULES = [
  {
    reason: 'user_logout',
    allowed: true,
    description: 'User explicitly clicked logout'
  },
  {
    reason: 'token_expired_confirmed',
    allowed: true,
    description: 'Backend confirmed token is expired (with refresh token also expired)'
  },
  {
    reason: 'network_error',
    allowed: false,
    description: 'Network connectivity issues'
  },
  {
    reason: 'server_error',
    allowed: false,
    description: 'Server errors (500, 503, etc.)'
  },
  {
    reason: 'api_error',
    allowed: false,
    description: 'Any API operation error'
  },
  {
    reason: 'booking_error',
    allowed: false,
    description: 'Errors during booking operations'
  },
  {
    reason: 'cart_error',
    allowed: false,
    description: 'Errors during cart operations'
  },
  {
    reason: 'auth_error',
    allowed: false,
    description: '401/403 errors (could be temporary)'
  }
];

/**
 * Check if token clearing is allowed for the given reason
 */
export function isTokenClearingAllowed(reason) {
  const rule = TOKEN_CLEARING_RULES.find(r => r.reason === reason);
  if (rule) {
    console.log(`🔍 Token clearing check for "${reason}": ${rule.allowed ? 'ALLOWED' : 'DENIED'}`);
    console.log(`   Reason: ${rule.description}`);
    return rule.allowed;
  }
  
  console.warn(`⚠️ Unknown token clearing reason: "${reason}" - DENYING by default`);
  return false;
}

/**
 * Safely clear token only if allowed
 */
export function safeClearToken(reason) {
  if (!isTokenClearingAllowed(reason)) {
    console.error(`❌ Token clearing BLOCKED for reason: "${reason}"`);
    console.error('   Tokens will be preserved. Use explicit logout to clear tokens.');
    return false;
  }
  
  console.log(`✅ Token clearing ALLOWED for reason: "${reason}"`);
  localStorage.removeItem('customerToken');
  localStorage.removeItem('token');
  localStorage.removeItem('tempCustomerToken');
  localStorage.removeItem('user');
  console.log('🧹 Tokens cleared successfully');
  return true;
}

/**
 * Get the current token without clearing it
 */
export function getToken() {
  const token = localStorage.getItem('customerToken') || 
                localStorage.getItem('token');
  
  if (token && token.trim().length > 0) {
    return token.trim();
  }
  
  return null;
}

/**
 * Set/update the token
 */
export function setToken(token) {
  if (token && token.trim().length > 0) {
    const cleanToken = token.trim();
    localStorage.setItem('customerToken', cleanToken);
    console.log('✅ Customer token stored successfully');
  } else {
    console.error('❌ Attempted to set empty/invalid token');
  }
}

/**
 * Handle error without clearing token
 */
export function handleErrorPreservingToken(error, context) {
  console.error(`❌ Error in ${context}:`, error);
  console.log('🔒 Token preserved despite error');
  
  // Check if this is an auth error
  if (error?.isAuthError || error?.status === 401 || error?.status === 403) {
    console.warn(`⚠️ Authentication error in ${context} - token PRESERVED`);
    console.warn('   User can retry the operation');
  }
}

/**
 * Setup global token preservation guards
 */
export function setupTokenPreservationGuards() {
  console.log('🛡️ Setting up customer token preservation guards...');
  
  // Override localStorage.removeItem to log token clearing attempts
  const originalRemoveItem = localStorage.removeItem.bind(localStorage);
  localStorage.removeItem = function(key) {
    if (key === 'customerToken' || key === 'token' || key === 'tempCustomerToken') {
      console.warn(`⚠️ Attempting to remove customer token: ${key}`);
      console.warn('   Stack trace:', new Error().stack);
    }
    return originalRemoveItem(key);
  };
  
  console.log('✅ Customer token preservation guards active');
}

export default {
  isTokenClearingAllowed,
  safeClearToken,
  getToken,
  setToken,
  handleErrorPreservingToken,
  setupTokenPreservationGuards,
};

